import { mobile } from "@btnet/utilities-webview";

/**
 * Converts a HEIC/HEIF file to a JPEG File object using the heic2any WASM library.
 *
 * Fragile: heic2any has known decode failures on certain iPhone HEIC variants and
 * is the reason we shifted mobile uploads to the native bridge. Prefer
 * `mobile.compressAndUpload` (gated by {@link canUseMobileHeicBridge}) when
 * running inside the mobile webview. This in-browser path remains for desktop
 * web and as a last-resort fallback when the bridge reports `HEIC_UNSUPPORTED`.
 */
export async function convertHeicToJpgInBrowser(file: File): Promise<File> {
    const { default: heic2any } = await import("heic2any");
    const blob = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.5 });
    const resultBlob = Array.isArray(blob) ? blob[0] : blob;
    const newName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
    return new File([resultBlob], newName, { type: "image/jpeg" });
}

const HEIC_BRANDS = new Set(["heic", "heix", "heim", "heis", "heiq", "mif1", "msf1"]);

async function hasHeicMagicBytes(file: File): Promise<boolean> {
    if (file.size < 12) return false;
    try {
        const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
        // HEIF/HEIC files start with a "ftyp" box at bytes 4-7, followed by a
        // 4-char brand at bytes 8-11 that identifies the codec inside.
        if (head[4] !== 0x66 || head[5] !== 0x74 || head[6] !== 0x79 || head[7] !== 0x70) {
            return false;
        }
        const brand = String.fromCharCode(head[8], head[9], head[10], head[11]);
        return HEIC_BRANDS.has(brand);
    } catch {
        return false;
    }
}

/**
 * Detects HEIC/HEIF content via extension, MIME type, then magic-byte sniff.
 * Async so the magic-byte path can read the file head when extension and MIME
 * are inconclusive (e.g., a HEIC file renamed to .jpg, or no extension).
 */
export async function isHeic(file: File): Promise<boolean> {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (ext === "heic" || ext === "heif") return true;
    if (file.type === "image/heic" || file.type === "image/heif") return true;
    return await hasHeicMagicBytes(file);
}

/**
 * True when the mobile webview shim exposes a bridge that can handle HEIC uploads.
 * Use this to route HEIC uploads through the native bridge instead of heic2any.
 * Accepts either the current `compressAndUpload` bridge or the legacy `convertHeicAndUpload`.
 */
export function canUseMobileHeicBridge(): boolean {
    return (
        mobile.isWebview &&
        (typeof mobile.compressAndUpload === "function" ||
            typeof mobile.convertHeicAndUpload === "function")
    );
}
